import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import type { BarSeriesOption, DefaultLabelFormatterCallbackParams } from 'echarts';

import { CHART_COLORS } from '../constants';
import type { TEChartsBarChartProps } from '../types';
import { buildCardAxisTooltipHtml, normalizeAxisTooltipParams, normalizeEChartsLabel } from '../utils';

const formatRuNumber = (value: number | string, valueFractionDigits?: number): string =>
  new Intl.NumberFormat(
    'ru-RU',
    valueFractionDigits != null
      ? { minimumFractionDigits: valueFractionDigits, maximumFractionDigits: valueFractionDigits }
      : undefined,
  ).format(Number(value));

export const datasetValueForSeries = (rawValue: unknown, seriesIndex: number): number | string => {
  if (Array.isArray(rawValue)) {
    const datasetSourceRow = rawValue.filter(
      (item): item is string | number => typeof item === 'string' || typeof item === 'number',
    );
    return datasetSourceRow[seriesIndex + 1] ?? datasetSourceRow[1] ?? 0;
  }

  return Number(rawValue);
};

const getNiceStep = (roughStep: number): number => {
  if (roughStep <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;

  const NICE_NORMALIZED_STEPS = [1, 2, 2.5, 5, 10];

  const niceNormalized =
    NICE_NORMALIZED_STEPS.find((step) => normalized <= step) ?? NICE_NORMALIZED_STEPS[NICE_NORMALIZED_STEPS.length - 1];

  return niceNormalized * magnitude;
};

/** Минимальный шаг оси, при котором лейблы с заданной точностью не схлопываются в одинаковые значения. */
const getMinIntervalByFractionDigits = (fractionDigits?: number): number | undefined => {
  if (fractionDigits == null || fractionDigits < 0 || !Number.isInteger(fractionDigits)) {
    return undefined;
  }

  return 10 ** -fractionDigits;
};

type TBarLabelPosition = Extract<
  NonNullable<NonNullable<BarSeriesOption['label']>['position']>,
  'top' | 'right' | 'inside'
>;

/**
 * Внутренний ключ категории (на оси/в tooltip показывается исходный labels[i]).
 * Без уникальных ключей одинаковые labels в dataset сливаются в одну категорию.
 */
const CATEGORY_KEY_PREFIX = '__echarts_cat_';

const toCategoryKey = (index: number): string => `${CATEGORY_KEY_PREFIX}${index}`;

type TUseEChartsBarChartResult = {
  getIsEmpty: ComputedRef<boolean>;
  getHasData: ComputedRef<boolean>;
  getDatasetSource: ComputedRef<(string | number)[][]>;
  getSeriesColors: ComputedRef<string[]>;
  formatValue: (value: number | string) => string;
  formatAxisLabel: (value: number | string) => string;
  formatCategoryAxisLabel: (categoryKey: unknown) => string;
  formatSeriesLabel: (labelFormatterParams: DefaultLabelFormatterCallbackParams) => string;
  buildAxisTooltipFormatter: (
    tooltipFormatterParams: DefaultLabelFormatterCallbackParams | DefaultLabelFormatterCallbackParams[],
  ) => string;
  buildBarSeries: (labelPosition: TBarLabelPosition) => BarSeriesOption[];
  getValueAxisBounds: (maxRatio?: number) => { max: number | undefined; interval: number | undefined };
};

const resolveStackId = (stack: boolean | string): string | undefined => {
  if (typeof stack === 'string') return stack.trim() || undefined;
  return stack ? 'stack' : undefined;
};

export const useEChartsBarChart = (props: TEChartsBarChartProps): TUseEChartsBarChartResult => {
  const getHasData = computed(() => props.dataset.flatMap((datasetItem) => datasetItem.data).length > 0);

  const getIsEmpty = computed(() => !getHasData.value && !props.isLoading);

  const getStackId = computed(() => resolveStackId(props.stack));

  const resolveLabelByIndex = (dataIndex: number, sourceLabels: string[]): string | undefined =>
    sourceLabels[dataIndex];

  const resolveCategoryLabel = (
    categoryKey: unknown,
    dataIndex?: number,
    sourceLabels: string[] = props.labels,
  ): string => {
    if (typeof dataIndex === 'number') {
      const labelByIndex = resolveLabelByIndex(dataIndex, sourceLabels);
      if (labelByIndex != null) return labelByIndex;
    }

    const key = typeof categoryKey === 'string' || typeof categoryKey === 'number' ? String(categoryKey) : '';
    if (key.startsWith(CATEGORY_KEY_PREFIX)) {
      const index = Number(key.slice(CATEGORY_KEY_PREFIX.length));
      if (Number.isInteger(index)) {
        const labelByKey = resolveLabelByIndex(index, sourceLabels);
        if (labelByKey != null) return labelByKey;
      }
    }

    return key;
  };

  const resolveTooltipCategoryLabel = (categoryKey: unknown, dataIndex?: number): string => {
    const tooltipLabels = props.tooltipLabels;
    if (tooltipLabels?.length) {
      return resolveCategoryLabel(categoryKey, dataIndex, tooltipLabels);
    }

    return resolveCategoryLabel(categoryKey, dataIndex);
  };

  const getDatasetSource = computed((): (string | number)[][] => [
    ['product', ...props.dataset.map((datasetItem) => normalizeEChartsLabel(datasetItem.label))],
    ...props.labels.map((_, labelIndex) => {
      const values = props.dataset.map((datasetItem) => datasetItem.data[labelIndex]);
      return [toCategoryKey(labelIndex), ...values];
    }),
  ]);

  const getSeriesColors = computed(() =>
    props.dataset.map((datasetItem, index) => datasetItem.color ?? CHART_COLORS[index]),
  );

  const formatValue = (value: number | string): string =>
    props.formatValueFunction ? props.formatValueFunction(value) : formatRuNumber(value, props.valueFractionDigits);

  const formatAxisLabel = (value: number | string): string =>
    props.formatLabelFunction
      ? props.formatLabelFunction(value)
      : `${formatRuNumber(value, props.valueFractionDigits)}${props.formatLabel}`;

  const formatCategoryAxisLabel = (categoryKey: unknown): string => resolveCategoryLabel(categoryKey);

  const formatSeriesLabel = (labelFormatterParams: DefaultLabelFormatterCallbackParams): string => {
    const value = datasetValueForSeries(labelFormatterParams.value, labelFormatterParams.seriesIndex ?? 0);
    if (getStackId.value && Number(value) === 0) return '';

    return formatValue(value);
  };

  const buildAxisTooltipFormatter = (
    tooltipFormatterParams: DefaultLabelFormatterCallbackParams | DefaultLabelFormatterCallbackParams[],
  ): string => {
    const listParams = normalizeAxisTooltipParams(tooltipFormatterParams);
    const orderedParams = props.tooltipInverseOrder ? [...listParams].reverse() : listParams;

    return buildCardAxisTooltipHtml(orderedParams, {
      getHeader: (headerParams) => {
        const firstParam = headerParams[0];
        const firstTooltipParamValue = firstParam.value;

        if (typeof firstParam.dataIndex === 'number') {
          return resolveTooltipCategoryLabel('', firstParam.dataIndex);
        }

        if (firstTooltipParamValue != null && Array.isArray(firstTooltipParamValue)) {
          return resolveTooltipCategoryLabel(firstTooltipParamValue[0]);
        }

        return resolveTooltipCategoryLabel(firstTooltipParamValue);
      },
      getSeriesLabel: (param, index) => {
        const seriesIndex = param.seriesIndex ?? index;
        return props.dataset[seriesIndex]?.label;
      },
      formatValue: (tooltipSeriesParam, index) => {
        const seriesIndex = tooltipSeriesParam.seriesIndex ?? index;
        const dataIndex = tooltipSeriesParam.dataIndex ?? 0;
        const value =getStackId.value && props.stackTooltipCumulative
            ? props.dataset
                .slice(0, seriesIndex + 1)
                .reduce((sum, datasetItem) => sum + (Number(datasetItem.data[dataIndex]) || 0), 0)
            : datasetValueForSeries(tooltipSeriesParam.value, seriesIndex);

        const formattedValue = props.formatValueFunction
          ? props.formatValueFunction(value, { seriesIndex, dataIndex })
          : formatRuNumber(value, props.valueFractionDigits);

        return `${formattedValue}${props.formatLabel}`;
      },
    });
  };

  const buildBarSeries = (labelPosition: TBarLabelPosition): BarSeriesOption[] => {
    const stackId = getStackId.value;

    return props.dataset.map((datasetItem) => {
      const barColors = datasetItem.colors;

      return {
        type: 'bar',
        name: normalizeEChartsLabel(datasetItem.label) || undefined,
        stack: stackId,
        barMaxWidth: props.barMaxWidth,
        ...(barColors?.length
          ? {
              itemStyle: {
                color: (barParams: { dataIndex: number }) => barColors[barParams.dataIndex % barColors.length],
              },
            }
          : {}),
        label: {
          show: props.showSeriesLabel,
          position: labelPosition,
          distance: 6,
          fontSize: 13,
          fontWeight: 600,
          color: props.labelColor,
          formatter: formatSeriesLabel,
        },
      };
    });
  };

  const getStackedCategoryMax = (): number => {
    const categoryCount = props.labels.length;
    if (!categoryCount) return 0;

    const categorySums = Array.from({ length: categoryCount }, (_, categoryIndex) =>
      props.dataset.reduce((sum, datasetItem) => sum + (Number(datasetItem.data[categoryIndex]) || 0), 0),
    );

    return Math.max(...categorySums, 0);
  };

  const getGroupedCategoryMax = (): number => {
    const values = props.dataset
      .flatMap((datasetItem) => datasetItem.data)
      .filter((value): value is number => typeof value === 'number');

    return values.length ? Math.max(...values) : 0;
  };

  const getValueAxisBounds = (maxRatio?: number): { max: number | undefined; interval: number | undefined } => {
    if (!getHasData.value || !maxRatio) {
      return { max: undefined, interval: undefined };
    }

    const dataMax = getStackId.value ? getStackedCategoryMax() : getGroupedCategoryMax();

    if (dataMax <= 0) {
      return { max: undefined, interval: undefined };
    }

    let interval = getNiceStep(dataMax / 8);
    const minInterval = getMinIntervalByFractionDigits(props.valueFractionDigits);
    if (minInterval != null && interval < minInterval) {
      interval = minInterval;
    }

    let max = Math.ceil((dataMax * maxRatio) / interval) * interval;
    if (max <= dataMax) max += interval;

    return { max, interval };
  };

  return {
    getIsEmpty,
    getHasData,
    getDatasetSource,
    getSeriesColors,
    formatValue,
    formatAxisLabel,
    formatCategoryAxisLabel,
    formatSeriesLabel,
    buildAxisTooltipFormatter,
    buildBarSeries,
    getValueAxisBounds,
  };
};
